import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import ReminderBanner from "@/components/ReminderBanner";
import FortuneCard from "@/components/FortuneCard";
import { useAuth } from "@/contexts/AuthContext";
import { usePageTitle } from "@/contexts/PageTitlesContext";
import { ArrowRight, Sparkles } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1651488829517-95af02975dd5?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

export default function Home() {
  const { t, i18n } = useTranslation();
  const zh = !i18n.language?.startsWith("en");
  const { user } = useAuth();
  const [featured, setFeatured] = useState([]);
  const hero = usePageTitle("home", {
    title_zh: t("home.heroTitle"), title_en: t("home.heroTitle"),
    subtitle_zh: t("home.heroSubtitle"), subtitle_en: t("home.heroSubtitle"),
  });

  useEffect(() => {
    api.get("/products", { params: { featured: true } })
      .then((r) => setFeatured(r.data.slice(0, 8)))
      .catch(() => setFeatured([]));
  }, []);

  return (
    <div>
      {/* Hero */}
      <section
        className="relative overflow-hidden"
        data-testid="home-hero"
        style={{ minHeight: "min(80vh, 720px)" }}
      >
        <img
          src={HERO_IMG}
          alt="JinLin hero"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(0,54,69,0.85) 0%, rgba(0,54,69,0.55) 45%, rgba(0,88,112,0.15) 100%)",
          }}
        />
        <div className="hero-glow" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10 pt-24 pb-32 md:pt-32 md:pb-44">
          <div className="max-w-2xl fade-up">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 px-4 py-1.5 text-xs font-medium text-white/90 tracking-widest">
              <Sparkles size={12} />
              JINLIN CREATIVE · 晶灵文创
            </div>
            <h1 className="mt-6 font-serif-cn text-4xl sm:text-5xl lg:text-6xl text-white font-semibold leading-[1.1] tracking-tight" data-testid="home-hero-title">
              {zh ? hero.title_zh : hero.title_en}
            </h1>
            <p className="mt-6 text-base md:text-lg text-white/85 leading-relaxed max-w-xl" data-testid="home-hero-subtitle">
              {zh ? hero.subtitle_zh : hero.subtitle_en}
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/products"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-[#005870] font-medium hover:bg-[#00a8a8] hover:text-white transition-colors duration-300"
                data-testid="hero-cta-shop"
              >
                {t("home.ctaShop")} <ArrowRight size={16} />
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/40 text-white font-medium hover:bg-white/10 transition-colors duration-300"
                data-testid="hero-cta-about"
              >
                {t("home.ctaAbout")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Reminder banner (if logged in + incomplete) */}
      {user && !user.profile_complete && user.role !== "admin" && (
        <section className="mx-auto max-w-7xl px-6 lg:px-10 mt-10">
          <ReminderBanner />
        </section>
      )}

      {/* Featured products — hide when empty for graceful empty state */}
      {featured.length > 0 && (
      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-20" data-testid="home-featured">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-12">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#00868e] font-semibold">
              featured
            </div>
            <h2 className="mt-2 font-serif-cn text-3xl md:text-4xl text-[#003645] font-semibold tracking-tight">
              {t("home.featured")}
            </h2>
            <p className="mt-3 text-[#4a6e77] max-w-xl leading-relaxed">
              {t("home.featuredDesc")}
            </p>
          </div>
          <Link
            to="/products"
            className="inline-flex items-center gap-2 text-sm font-medium text-[#005870] hover:text-[#00868e] transition-colors duration-200"
            data-testid="featured-view-all"
          >
            {i18n.language.startsWith("en") ? "View all" : "查看全部"} <ArrowRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-12">
          {featured.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </section>
      )}

      {/* Today's fortune */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-16">
        <FortuneCard />
      </section>
    </div>
  );
}
